// src/pages/Documents.jsx
// Resources → Documents (3.68.0, owner, Oct 26): a client's signed contracts
// (Service Agreement, BAA, NDA and others). Only Super Admins (any client,
// picked here) and the client's Master Admins (their own company) see this
// page, and they can only view and download: each document opens inside the
// portal (DocumentViewer). Uploading, describing, renaming and deleting stay
// in Console → Clients (Super Admins). Backend: routes/documents.js.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE } from '../firebase/config';
import { PAGE_TITLE, SECTION_SUB } from '../theme/tabs';
import Loader from '../components/Loader';
import Emo from '../components/Emo';
import Pager, { usePager } from '../components/Pager';
import DocumentViewer from '../components/DocumentViewer';
import SortTh, { useSort } from '../components/SortTh';
import { usePhone } from '../hooks/useWindowWidth';

const NONE = [];
const fmtDay = d => (d ? new Date(`${d.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const kb = n => (!n ? '' : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const soon = d => d && d <= new Date(Date.now() + 30 * 86400e3).toISOString().slice(0, 10);

export default function Documents() {
  const { getToken, effectiveInfo, userInfo } = useAuth();
  const superView = userInfo?.role === 'super_admin' || effectiveInfo?.level === 'super_admin';
  const clients = useMemo(() => (effectiveInfo?.accounts || []).filter(c => c.type !== 'individual' || superView), [effectiveInfo, superView]);
  const picked = effectiveInfo?.accountId && effectiveInfo.accountId !== 'all' ? effectiveInfo.accountId : '';
  const [account, setAccount] = useState(picked || '');
  useEffect(() => { if (picked) setAccount(picked); }, [picked]);
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState(null);
  const [filter, setFilter] = useState('current');
  const [show, setShow] = useState(25);
  const phone = usePhone();   // phones: one card per document, not a table

  const q = superView ? `account=${encodeURIComponent(account)}` : '';
  const load = useCallback(async () => {
    if (superView && !account) { setDocs(null); return; }
    setDocs(null); setError('');
    try {
      const r = await fetch(`${API_BASE}/portal/documents?${q}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't load the documents.");
      setDocs(j.data || []);
    } catch (e) { setError(e.message); }
  }, [q, superView, account, getToken]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const list = (docs || NONE).filter(d => filter === 'all' || (filter === 'current' ? d.status !== 'replaced' : d.status === 'replaced'));
    const when = d => d.signedOn || d.uploadedAt || d.storedAt || '';
    return [...list].sort((a, b) => String(when(b)).localeCompare(String(when(a))));   // newest first
  }, [docs, filter]);
  // 3.68.3 (owner): click a column header to sort by it, again to reverse
  const { sorted, sort, onSort } = useSort(shown, {
    doc: d => (d.type === 'other' && d.label ? d.label : d.typeLabel), file: d => d.fileName,
    signed: d => d.signedOn || '', renews: d => d.expiresOn || '',
  });
  const { pageRows, pager } = usePager(sorted, show);
  // 3.68.3 (owner): a click anywhere on a document's row (or card) opens it; Enter too
  const openProps = d => ({ onClick: () => setViewing(d), onKeyDown: e => { if (e.key === 'Enter') setViewing(d); }, tabIndex: 0, 'aria-label': `Open ${d.fileName}`, className: 'doc-row' });
  const fileUrl = d => `${API_BASE}/portal/documents/file?${q ? `${q}&` : ''}path=${encodeURIComponent(d.path)}`;

  return (
    <div>
      <h2 style={PAGE_TITLE} className="cp-title"><Emo e="📜" /> Documents</h2>
      <p style={{ ...SECTION_SUB, marginBottom: 14 }}>
        {superView
          ? <>A client's signed documents (Service Agreement, BAA, NDA and others), as their Master Admins see them. Open one to read it here, or download it.
            Upload, rename and delete documents in Console → Clients.</>
          : <>Your company's signed documents, such as the Service Agreement, BAA or NDA. Open one to preview it, or download it.
            If something is missing or needs updating, contact the support team.</>}
      </p>

      <section style={S.panel}>
        <div style={S.bar}>
          {superView && (
            <label style={S.barLabel}>Client
              <select value={account} onChange={e => setAccount(e.target.value)} style={S.select} aria-label="Client">
                <option value="">Pick a client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )}
          <label style={S.barLabel}>Show
            <select value={filter} onChange={e => setFilter(e.target.value)} style={S.select} aria-label="Which documents">
              <option value="current">Current</option>
              <option value="replaced">Replaced by a newer one</option>
              <option value="all">All</option>
            </select>
          </label>
          <label style={S.barLabel}>Per page
            <select value={show} onChange={e => setShow(Number(e.target.value))} style={S.select} aria-label="Documents per page">
              {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>

        {superView && !account ? <div style={S.empty}>Pick a client to see its documents.</div>
          : error ? <div role="alert" style={S.error}>{error}</div>
          : !docs ? <Loader label="Loading documents…" />
          : phone ? (
            <div style={S.cards}>
              {!shown.length && <div style={S.empty}>{docs.length ? 'None match.' : 'No documents yet.'}</div>}
              {pageRows.map(d => (
                <div key={d.path} style={{ ...S.card, cursor: 'pointer' }} {...openProps(d)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0 }}>
                      <strong>{d.type === 'other' && d.label ? d.label : d.typeLabel}</strong>
                      {d.status === 'replaced' && <span style={{ ...S.pill, marginLeft: 8 }}>Replaced</span>}
                      <div style={{ ...S.dim, overflowWrap: 'anywhere', marginTop: 2 }}>{d.fileName}{d.size ? ` · ${kb(d.size)}` : ''}</div>
                    </div>
                    <button onClick={e => { e.stopPropagation(); setViewing(d); }} tabIndex={-1} style={S.btn}><Emo e="👁️" /> View</button>
                  </div>
                  <div style={{ ...S.dim, marginTop: 6 }}>
                    Signed {fmtDay(d.signedOn)}{d.expiresOn ? <> · <span style={d.status !== 'replaced' && soon(d.expiresOn) ? { color: 'var(--t-e65100)', fontWeight: 700 } : undefined}>renews or ends {fmtDay(d.expiresOn)}</span></> : ''}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead><tr>
                  <SortTh k="doc" label="Document" sort={sort} onSort={onSort} style={S.th} />
                  <SortTh k="file" label="File" sort={sort} onSort={onSort} style={S.th} />
                  <SortTh k="signed" label="Signed" sort={sort} onSort={onSort} style={S.th} />
                  <SortTh k="renews" label="Renews or ends" sort={sort} onSort={onSort} style={S.th} />
                  <th style={S.th} />
                </tr></thead>
                <tbody>
                  {!shown.length && <tr><td colSpan={5} style={S.empty}>{docs.length ? 'None match.' : 'No documents yet.'}</td></tr>}
                  {pageRows.map(d => (
                    <tr key={d.path} style={{ cursor: 'pointer' }} {...openProps(d)}>
                      <td style={S.td}>
                        <strong>{d.type === 'other' && d.label ? d.label : d.typeLabel}</strong>
                        {d.status === 'replaced' && <span style={{ ...S.pill, marginLeft: 8 }}>Replaced</span>}
                      </td>
                      <td style={{ ...S.td, color: 'var(--t-546e7a)', overflowWrap: 'anywhere' }}>{d.fileName}{d.size ? <span style={S.dim}> · {kb(d.size)}</span> : null}</td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtDay(d.signedOn)}</td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap', ...(d.status !== 'replaced' && soon(d.expiresOn) ? { color: 'var(--t-e65100)', fontWeight: 700 } : {}) }}>{fmtDay(d.expiresOn)}</td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button onClick={e => { e.stopPropagation(); setViewing(d); }} tabIndex={-1} style={S.btn}><Emo e="👁️" /> View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        {!!shown.length && <Pager {...pager} label="documents" />}
      </section>
      {viewing && <DocumentViewer doc={viewing} fileUrl={fileUrl(viewing)} onClose={() => setViewing(null)} />}
      <style>{'.doc-row:hover { background: var(--c-f4f7f6); } .doc-row:focus-visible { outline: 2px solid var(--c-00897b); outline-offset: -2px; }'}</style>
    </div>
  );
}

const S = {
  panel: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 14, padding: '16px 18px' },
  bar: { display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 },
  barLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--t-546e7a)', fontWeight: 600 },
  select: { padding: '6px 10px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 13, fontFamily: 'inherit', maxWidth: '100%' },
  tableWrap: { overflowX: 'auto', marginTop: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', background: 'var(--c-00695c)', color: 'var(--t-ffffff)', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid var(--c-eceff1)', verticalAlign: 'middle' },
  empty: { padding: '18px 10px', color: 'var(--t-90a4ae)', textAlign: 'center' },
  error: { padding: '12px 14px', borderRadius: 10, background: 'var(--c-ffebee)', color: 'var(--t-c62828)', fontSize: 13 },
  pill: { display: 'inline-block', padding: '2px 9px', borderRadius: 10, fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', color: 'var(--t-546e7a)', background: 'var(--c-eceff1)' },
  dim: { color: 'var(--t-90a4ae)', fontSize: 12.5 },
  cards: { display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 },
  card: { border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '12px 14px', fontSize: 13.5 },
  btn: {
    padding: '5px 12px', borderRadius: 8, border: '1.5px solid var(--c-b2dfdb)', background: 'var(--c-ffffff)',
    color: 'var(--t-00695c)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
};
